import React, { useEffect, useState, useLayoutEffect } from 'react';
import { CaseStudyHeader } from '../components/CaseStudyHeader';
import { CaseStudyMobileHeader } from '../components/CaseStudyMobileHeader';
import { Footer } from '../components/Footer';
import { CustomCursor } from '../components/CustomCursor';
import { CaseStudyNavigation } from '../components/CaseStudyNavigation';
import { ChatDrawer } from '../components/ChatDrawer';
import { FloatingButtons } from '../components/FloatingButtons';
import { Link, useNavigate } from 'react-router-dom';
import { usePortfolio } from '../context/PortfolioContext';
import { useCaseStudyNavigation } from '../hooks/useCaseStudyNavigation';
import { ArrowLeft, ArrowRight, ArrowDown, Image, CheckCircle, Users, Database, Rocket, Target, ChatCircle, NavigationArrow, Stack, Clock, TrendUp, Lightbulb, Gear, Compass } from '@phosphor-icons/react';
import { useScrollAnimation } from '../utils/AnimationObserver';
import '../styles/animations.css';
import { LightboxImage } from '../components/LightboxImage';
interface CaseStudyCyberSecurityProps {
  isOverlay?: boolean;
}
export function CaseStudyCyberSecurity({
  isOverlay = false
}: CaseStudyCyberSecurityProps) {
  const {
    viewPreference,
    isChatDrawerOpen,
    navigatedFromChat
  } = usePortfolio();
  const navigate = useNavigate();
  // Check if user came from chat OR home page
  const cameFromChat = sessionStorage.getItem('cameFromChat') === 'true';
  const cameFromHome = sessionStorage.getItem('navigatedFromHome') === 'true';
  const showBackButton = cameFromChat || cameFromHome;
  const {
    openChatDrawer,
    previousPage,
    setPreviousPage
  } = usePortfolio();
  // Handle back navigation to home
  const handleBackToHome = () => {
    // Clear navigation flags
    sessionStorage.removeItem('cameFromChat');
    sessionStorage.removeItem('navigatedFromHome');
    // Reset scroll position IMMEDIATELY before navigation
    window.scrollTo(0, 0);
    const homePath = viewPreference === 'ai' ? '/portfolio/ai' : '/portfolio/traditional';
    navigate(homePath);
  };
  // Handle chat state when navigating to this case study
  useCaseStudyNavigation();
  // Determine home path based on view preference
  const homePath = viewPreference === 'ai' ? '/portfolio/ai' : '/portfolio/traditional';
  // Initialize scroll animations
  useScrollAnimation();
  // Ensure page starts at top - CRITICAL: Run before any rendering
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
  }, []);
  // Active section state for sticky navigation
  const [activeSection, setActiveSection] = useState('overview');
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['overview', 'situation', 'task', 'action', 'results'];
      const scrollPosition = window.scrollY + 200;
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(section);
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 100,
        behavior: 'smooth'
      });
    }
  };
  // Dummy handler for ChatDrawer (navigation handled inside ChatDrawer)
  const handleCaseStudyClick = (slug: string) => {
    console.log('Case study navigation handled by ChatDrawer:', slug);
  };
  const navItems = [{
    id: 'overview',
    label: 'Overview'
  }, {
    id: 'situation',
    label: 'Situation'
  }, {
    id: 'task',
    label: 'Task'
  }, {
    id: 'action',
    label: 'Action'
  }, {
    id: 'results',
    label: 'Results'
  }];
  return <>
      {!isOverlay && <CustomCursor data-id="element-1008" />}

      <div className="flex min-h-screen bg-white text-black font-switzer" data-id="element-1009">
        <div className="flex-1 min-w-0 flex flex-col relative" data-id="element-1010">
          {!isOverlay && <CaseStudyHeader data-id="element-1011" />}
          {!isOverlay && <CaseStudyMobileHeader data-id="element-1012" />}

          {/* Hero Section - Clean White Background */}
          <section className="pt-32 pb-12 px-4 sm:px-8 md:px-12 lg:px-24 max-w-[1400px] mx-auto w-full" data-id="element-1013">
            <div className="max-w-4xl" data-id="element-1014">
              {/* Back to Portfolio button - always visible */}
              {!isOverlay && <button onClick={handleBackToHome} className="flex items-center gap-2 text-gray-600 hover:text-[#016768] transition-colors mb-6 group" aria-label="Back to portfolio" style={{
              marginBottom: '28px'
            }} data-id="element-1015">
                  <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" data-id="element-1016" />
                  <span className="text-sm font-medium" data-id="element-1017">Back to Portfolio</span>
                </button>}

              <h1 className="text-5xl sm:text-6xl md:text-7xl font-light tracking-tight mb-8 text-[#016768] leading-[1.1]" data-id="element-1018">
                Dialexa: Unisys
              </h1>
              <p className="text-xl sm:text-2xl text-gray-600 leading-relaxed max-w-2xl font-light" data-id="element-1019">
                Agency project for Unisys, transforming complex micro
                segmentation and threat analysis applications into intuitive,
                user-centered solutions.
              </p>
            </div>
          </section>

          {/* Full Width Hero Image */}
          <div className="w-full h-[60vh] sm:h-[80vh] overflow-hidden mb-24" data-id="element-1020">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/odCcwZqxVnrHSvVy5xeJrh/unisys_thumb.png" alt="Unisys Stealth Hero" className="w-full h-full object-cover object-center" data-id="element-1021" />
          </div>

          {/* Main Content Grid */}
          <div className="max-w-[1400px] mx-auto w-full px-4 sm:px-8 md:px-12 lg:px-24 pb-32" data-id="element-1022">
            <div className="lg:grid lg:grid-cols-12 gap-12" data-id="element-1023">
              {/* Sticky Navigation Sidebar */}
              <div className="hidden lg:block lg:col-span-3" data-id="element-1024">
                <div className="sticky top-32 space-y-6" data-id="element-1025">
                  {navItems.map(item => <button key={item.id} onClick={() => scrollToSection(item.id)} className={`block text-sm tracking-wide transition-colors duration-300 ${activeSection === item.id ? 'text-[#016768] font-medium border-l-2 border-[#016768] pl-4' : 'text-gray-400 hover:text-gray-600 pl-4 border-l-2 border-transparent'}`} data-id="element-1026">
                      {item.label}
                    </button>)}
                </div>
              </div>

              {/* Content Column */}
              <div className="lg:col-span-9 space-y-32" data-id="element-1027">
                {/* Overview Section */}
                <section id="overview" className="scroll-mt-32 -mx-4 sm:-mx-8 md:-mx-12 lg:-mx-24 px-4 sm:px-8 md:px-12 lg:px-24 py-16 bg-gray-50" data-id="element-1028">
                  <div className="grid md:grid-cols-3 gap-16 mb-16" data-id="element-1029">
                    <div data-id="element-1030">
                      <h3 className="text-sm font-medium text-[#016768] uppercase tracking-wider mb-2" data-id="element-1031">
                        Role
                      </h3>
                      <p className="text-lg text-slate-700" data-id="element-1032">Lead UX Designer</p>
                    </div>
                    <div data-id="element-1033">
                      <h3 className="text-sm font-medium text-[#016768] uppercase tracking-wider mb-2" data-id="element-1034">
                        Project Type
                      </h3>
                      <p className="text-lg text-slate-700" data-id="element-1035">Agency Project</p>
                    </div>
                    <div data-id="element-1036">
                      <h3 className="text-sm font-medium text-[#016768] uppercase tracking-wider mb-2" data-id="element-1037">
                        Client
                      </h3>
                      <p className="text-lg text-slate-700" data-id="element-1038">
                        Unisys (via Dialexa)
                      </p>
                    </div>
                  </div>

                  <h2 className="text-3xl font-light mb-8 text-[#016768]" data-id="element-1039">
                    Overview
                  </h2>
                  <p className="text-lg text-slate-600 leading-relaxed mb-12 max-w-3xl" data-id="element-1040">
                    Working at Dialexa as a consultant, I was tasked with
                    redesigning Unisys Stealth (Aware), a complex cybersecurity
                    platform that provides{' '}
                    <span className="font-semibold text-slate-900" data-id="element-1041">
                      micro segmentation and threat analysis capabilities
                    </span>{' '}
                    for enterprise clients. The project involved{' '}
                    <span className="font-semibold text-slate-900" data-id="element-1042">
                      upgrading from version 5 to version 6
                    </span>
                    , transforming it into a competitive, user-friendly platform
                    that would impress in sales meetings.
                  </p>

                  <div className="bg-white rounded-2xl p-8 md:p-12 mb-12" data-id="element-1043">
                    <h3 className="text-xl font-medium mb-8 text-[#016768]" data-id="element-1044">
                      Key Achievements
                    </h3>
                    <div className="space-y-4" data-id="element-1045">
                      {['Successfully <span class="font-semibold text-slate-900">redesigned complex cybersecurity workflows</span> for improved usability while maintaining technical functionality', 'Developed a <span class="font-semibold text-slate-900">comprehensive design system with updated components</span> that aligned with modern UI standards', 'Influenced <span class="font-semibold text-slate-900">strategic business decisions through competitive analysis</span> and industry research', 'Successfully <span class="font-semibold text-slate-900">positioned Unisys better against competitors like Illumio</span> through strategic design recommendations'].map((item, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-1046">
                          <CheckCircle className="w-5 h-5 text-[#016768] mt-1 flex-shrink-0" data-id="element-1047" />
                          <span className="text-slate-700 text-lg" dangerouslySetInnerHTML={{
                        __html: item
                      }} data-id="element-1048" />
                        </div>)}
                    </div>
                  </div>

                  <div className="mb-16" data-id="element-1049">
                    <LightboxImage src="https://cdn.magicpatterns.com/uploads/8Z9WqxDbqCWDcRCMWd5zyF/Unisys.png" alt="Unisys Stealth dashboard interface" label="Stealth Dashboard" data-id="element-1050" />
                  </div>
                </section>

                {/* Situation Section */}
                <section id="situation" className="scroll-mt-32" data-id="element-1051">
                  <h2 className="text-3xl font-light mb-12 text-[#016768]" data-id="element-1052">
                    User Problem & Research Challenge
                  </h2>

                  <p className="text-lg text-slate-600 leading-relaxed mb-12 max-w-3xl" data-id="element-1053">
                    Unisys needed to upgrade their Stealth (Aware) cybersecurity
                    product from version 5 to version 6, transforming it into a
                    competitive, user-friendly platform. Version 5, while the
                    latest GUI update designed to help teams model and deploy
                    security plans,{' '}
                    <span className="font-semibold text-slate-900" data-id="element-1054">
                      hadn't been thoroughly tested and was failing to impress
                      in sales meetings.
                    </span>
                  </p>

                  {/* Problem Statement */}
                  <div className="mb-16 border-l-4 border-[#016768] pl-8 py-2" data-id="element-1055">
                    <h3 className="text-sm font-bold text-[#016768] uppercase tracking-wider mb-4" data-id="element-1056">
                      Problem Statement
                    </h3>
                    <p className="text-2xl md:text-3xl font-light leading-tight text-slate-900" data-id="element-1057">
                      Security professionals working in{' '}
                      <span className="font-semibold text-slate-900" data-id="element-1058">
                        low-lit environments
                      </span>{' '}
                      needed to quickly analyze threats and configure
                      micro-segmentation policies, but Stealth's complex
                      interface with technical jargon, poor visual hierarchy,
                      and lack of intuitive workflows{' '}
                      <span className="font-semibold text-slate-900" data-id="element-1059">
                        increased cognitive load and slowed critical security
                        decisions.
                      </span>
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-8 mb-16" data-id="element-1060">
                    {[{
                    title: 'Sales Impact',
                    desc: '"We go into sales meetings trying to secure a second meeting, but the first has to impress. Right now, we\'re not getting that response." - Unisys Business Lead'
                  }, {
                    title: 'Technical Complexity',
                    desc: 'The platform dealt with complex cybersecurity concepts like micro-segmentation that required significant domain knowledge to understand and design for.'
                  }, {
                    title: 'Specialized Users',
                    desc: 'The product was used by highly technical cybersecurity professionals with specific needs and workflows.'
                  }, {
                    title: 'Multiple User Flows',
                    desc: 'The project required addressing multiple user flows across two distinct products within the Stealth ecosystem.'
                  }].map((card, idx) => <div key={idx} className="bg-gray-50 p-8 rounded-xl" data-id="element-1061">
                        <h4 className="font-medium text-lg mb-2 text-[#016768]" data-id="element-1062">
                          {card.title}
                        </h4>
                        <p className="text-slate-600" data-id="element-1063">{card.desc}</p>
                      </div>)}
                  </div>

                  <div className="mb-12" data-id="element-1064">
                    <LightboxImage src="https://cdn.magicpatterns.com/uploads/asQNUakddLpBCPbCr2XyP6/Stealth-image-1.jpg" alt="Unisys Stealth v5 interface" label="Stealth v5 Interface" data-id="element-1065" />
                  </div>
                </section>

                {/* Task Section */}
                <section id="task" className="scroll-mt-32" data-id="element-1066">
                  <h2 className="text-3xl font-light mb-12 text-[#016768]" data-id="element-1067">
                    Impact
                  </h2>

                  <p className="text-lg text-slate-600 leading-relaxed mb-12 max-w-3xl" data-id="element-1068">
                    The primary objective was to redesign Unisys Stealth v6 to
                    be competitive with market leaders in the cybersecurity
                    space, creating a platform that wins sales meetings and
                    impresses stakeholders with its design and usability.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16" data-id="element-1069">
                    {[{
                    icon: Target,
                    title: 'Competitive Redesign',
                    desc: 'Redesign Unisys Stealth v6 to be competitive with market leaders in the cybersecurity space'
                  }, {
                    icon: Users,
                    title: 'User Experience',
                    desc: 'Improve user experience for cybersecurity professionals working with complex security systems'
                  }, {
                    icon: TrendUp,
                    title: 'Sales Impact',
                    desc: 'Create a platform that wins sales meetings and impresses stakeholders'
                  }, {
                    icon: Gear,
                    title: 'Technical Balance',
                    desc: 'Balance technical functionality with improved usability across multiple user flows'
                  }].map((item, idx) => <div key={idx} className="bg-gray-50 p-8 rounded-xl border border-gray-100" data-id="element-1070">
                        <div className="w-10 h-10 rounded-lg bg-[#016768] flex items-center justify-center text-white mb-4" data-id="element-1071">
                          <item.icon className="w-5 h-5" data-id="element-1072" />
                        </div>
                        <h4 className="text-lg font-semibold text-slate-900 mb-2" data-id="element-1073">
                          {item.title}
                        </h4>
                        <p className="text-base text-slate-600 leading-relaxed" data-id="element-1074">
                          {item.desc}
                        </p>
                      </div>)}
                  </div>

                  <div className="mb-12" data-id="element-1075">
                    <LightboxImage src="https://cdn.magicpatterns.com/uploads/phrEbZmX3K61nFkcineH7E/Timeline.png" alt="Stealth UI Design Project Plan" label="Project Timeline" data-id="element-1076" />
                  </div>
                </section>

                {/* Action Section */}
                <section id="action" className="scroll-mt-32" data-id="element-1077">
                  <h2 className="text-3xl font-light mb-12 text-[#016768]" data-id="element-1078">
                    Approach & Execution
                  </h2>

                  <div className="space-y-24" data-id="element-1079">
                    {/* Execution - Research */}
                    <div data-id="element-1080">
                      <div className="flex items-center gap-4 mb-6" data-id="element-1081">
                        <span className="text-4xl font-light text-slate-300" data-id="element-1082">
                          01
                        </span>
                        <h3 className="text-2xl font-medium text-[#016768]" data-id="element-1083">
                          Strategic Research & Discovery
                        </h3>
                      </div>

                      <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-3xl" data-id="element-1084">
                        I conducted a comprehensive heuristic evaluation of the
                        entire Stealth platform, systematically analyzing each
                        screen against established usability principles. This
                        process involved breaking down the interface into
                        discrete components and identifying quick wins that
                        could be presented to stakeholders for immediate impact.
                      </p>

                      <div className="mb-8" data-id="element-1085">
                        <LightboxImage src="https://cdn.magicpatterns.com/uploads/gWvkJF6TBtxRD48ugYuk6r/Heuristic_Evaluation_2_1.png" alt="Heuristic Evaluation of Unisys Stealth Aware 5.0" label="Heuristic Evaluation" data-id="element-1086" />
                      </div>

                      <div className="bg-gray-50 rounded-2xl p-8 mb-12 border border-gray-100" data-id="element-1087">
                        <h4 className="text-xl font-medium mb-6 text-[#016768]" data-id="element-1088">
                          User Research Findings
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1089">
                          <div className="bg-white p-5 rounded-lg border border-gray-100" data-id="element-1090">
                            <h5 className="font-medium mb-2 text-[#016768]" data-id="element-1091">
                              Primary User Context
                            </h5>
                            <p className="text-slate-600" data-id="element-1092">
                              Works 8 hours/day in low-lit settings, uses
                              command line in dark mode alongside Stealth
                            </p>
                          </div>
                          <div className="bg-white p-5 rounded-lg border border-gray-100" data-id="element-1093">
                            <h5 className="font-medium mb-2 text-[#016768]" data-id="element-1094">
                              Core Needs
                            </h5>
                            <p className="text-slate-600" data-id="element-1095">
                              Cut down eye strain, intuitive layout with minimal
                              jargon, quick export capabilities
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Execution - Competitive Analysis */}
                    <div data-id="element-1096">
                      <div className="flex items-center gap-4 mb-6" data-id="element-1097">
                        <span className="text-4xl font-light text-slate-300" data-id="element-1098">
                          02
                        </span>
                        <h3 className="text-2xl font-medium text-[#016768]" data-id="element-1099">
                          Competitive Analysis & Strategic Recommendations
                        </h3>
                      </div>

                      <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-3xl" data-id="element-1100">
                        Conducted in-depth competitive analysis of market
                        leaders, comparing{' '}
                        <span className="font-semibold text-slate-900" data-id="element-1101">
                          Unisys's 200-page microsegmentation documentation to
                          competitor's 80-page summary.
                        </span>{' '}
                        Presented findings to leadership, highlighting that
                        competitors simplified offerings with a{' '}
                        <span className="font-semibold text-slate-900" data-id="element-1102">
                          "caveman could do it" approach.
                        </span>
                      </p>

                      <div className="bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm mb-8" data-id="element-1103">
                        <div style={{
                        padding: '56.25% 0 0 0',
                        position: 'relative'
                      }} data-id="element-1104">
                          <iframe src="https://player.vimeo.com/video/1144651751?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479" frameBorder="0" allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" referrerPolicy="strict-origin-when-cross-origin" style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%'
                        }} title="Cyberoverview" data-id="element-1105"></iframe>
                        </div>
                        <p className="text-sm text-gray-500 p-4 text-center bg-gray-50 border-t border-gray-100" data-id="element-1106">
                          Educational video showing how other companies approach
                          security infrastructure
                        </p>
                      </div>
                    </div>

                    {/* Execution - Design & Development */}
                    <div data-id="element-1107">
                      <div className="flex items-center gap-4 mb-6" data-id="element-1108">
                        <span className="text-4xl font-light text-slate-300" data-id="element-1109">
                          03
                        </span>
                        <h3 className="text-2xl font-medium text-[#016768]" data-id="element-1110">
                          Design & Development Collaboration
                        </h3>
                      </div>

                      <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-3xl" data-id="element-1111">
                        Worked closely with Unisys's internal design team to
                        create detailed user personas and journey maps.
                        Developed solutions addressing identified pain points
                        while balancing user needs with technical constraints.
                      </p>

                      <div className="mb-8" data-id="element-1112">
                        <LightboxImage src="https://cdn.magicpatterns.com/uploads/iyfZFxYsEpmMRL3FTtStEF/Journey_Map_Unisys.png" alt="Unisys Stealth UI Journey Map" label="User Journey Map" data-id="element-1113" />
                      </div>

                      <div className="bg-gray-50 rounded-2xl p-8 border border-gray-100" data-id="element-1114">
                        <h4 className="text-xl font-medium mb-6 text-[#016768]" data-id="element-1115">
                          Design Deliverables
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-1116">
                          <div className="bg-white p-5 rounded-lg border border-gray-100" data-id="element-1117">
                            <h5 className="font-medium mb-2 text-[#016768]" data-id="element-1118">
                              Complete UI Redesign
                            </h5>
                            <p className="text-slate-600" data-id="element-1119">
                              Full redesign for Stealth v6 with improved
                              usability and visual design
                            </p>
                          </div>
                          <div className="bg-white p-5 rounded-lg border border-gray-100" data-id="element-1120">
                            <h5 className="font-medium mb-2 text-[#016768]" data-id="element-1121">
                              Custom Security Dashboard
                            </h5>
                            <p className="text-slate-600" data-id="element-1122">
                              Intuitive dashboard for monitoring and managing
                              security policies
                            </p>
                          </div>
                        </div>
                        <LightboxImage src="https://cdn.magicpatterns.com/uploads/tD1r8ynrxrAuLtn92ee2hj/Threatdashboard.png" alt="Unisys Stealth Security Dashboard" label="Threat Dashboard Design" data-id="element-1123" />
                      </div>
                    </div>
                  </div>
                </section>

                {/* Results Section */}
                <section id="results" className="scroll-mt-32" data-id="element-1124">
                  <h2 className="text-3xl font-light mb-12 text-[#016768]" data-id="element-1125">
                    Outcomes & Key Insights
                  </h2>

                  <div className="mb-12" data-id="element-1126">
                    <div className="bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm mb-8" data-id="element-1127">
                      <div style={{
                      padding: '56.25% 0 0 0',
                      position: 'relative'
                    }} data-id="element-1128">
                        <iframe src="https://player.vimeo.com/video/476985592?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479" frameBorder="0" allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" referrerPolicy="strict-origin-when-cross-origin" style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%'
                      }} title="Stealth(core)™ Demonstration" data-id="element-1129"></iframe>
                      </div>
                      <p className="text-sm text-gray-500 p-4 text-center bg-gray-50 border-t border-gray-100" data-id="element-1130">
                        Unisys Stealth Design Showcase - Final Implementation
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16" data-id="element-1131">
                    {[{
                    icon: ChatCircle,
                    title: 'Stakeholder Buy-in',
                    desc: 'Client feedback confirmed presentation sparked meaningful discussions about change'
                  }, {
                    icon: Lightbulb,
                    title: 'Strategic Validation',
                    desc: 'Complexity-reduction message resonated deeply with leadership'
                  }, {
                    icon: Compass,
                    title: 'Design Direction',
                    desc: 'User insights and competitive analysis directly shaped redesign approach'
                  }].map((item, idx) => <div key={idx} className="bg-gray-50 p-6 rounded-xl border border-gray-100" data-id="element-1132">
                        <div className="w-10 h-10 bg-[#016768] rounded-full flex items-center justify-center mb-4 text-white" data-id="element-1133">
                          <item.icon className="w-5 h-5" data-id="element-1134" />
                        </div>
                        <h4 className="text-lg font-semibold mb-2 text-[#016768]" data-id="element-1135">
                          {item.title}
                        </h4>
                        <p className="text-sm text-gray-600" data-id="element-1136">{item.desc}</p>
                      </div>)}
                  </div>

                  <div className="bg-[#016768] text-white p-8 sm:p-12 rounded-xl sm:rounded-2xl relative mb-16" data-id="element-1137">
                    <div className="flex items-center gap-3 mb-8 sm:mb-12" data-id="element-1138">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/10 rounded-xl flex items-center justify-center" data-id="element-1139">
                        <TrendUp className="w-5 h-5 sm:w-6 sm:h-6 text-white" data-id="element-1140" />
                      </div>
                      <h3 className="text-xl sm:text-2xl font-light" data-id="element-1141">
                        Long-term Validation
                      </h3>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1142">
                      <div className="bg-white/10 p-6 sm:p-8 rounded-xl backdrop-blur-sm border border-white/10 hover:bg-white/15 transition-all duration-300 flex flex-col justify-between h-full" data-id="element-1143">
                        <div className="mb-4" data-id="element-1144">
                          <Target className="w-8 h-8 sm:w-10 sm:h-10 text-white/80" data-id="element-1145" />
                        </div>
                        <div data-id="element-1146">
                          <h4 className="text-lg sm:text-xl font-bold mb-3" data-id="element-1147">
                            Industry Prediction
                          </h4>
                          <p className="text-sm sm:text-base text-white/80 leading-relaxed" data-id="element-1148">
                            Recommended CrowdStrike partnership was later
                            implemented, validating the strategic direction
                            proposed during the design phase.
                          </p>
                        </div>
                      </div>
                      <div className="bg-white/10 p-6 sm:p-8 rounded-xl backdrop-blur-sm border border-white/10 hover:bg-white/15 transition-all duration-300 flex flex-col justify-between h-full" data-id="element-1149">
                        <div className="mb-4" data-id="element-1150">
                          <TrendUp className="w-8 h-8 sm:w-10 sm:h-10 text-white/80" data-id="element-1151" />
                        </div>
                        <div data-id="element-1152">
                          <h4 className="text-lg sm:text-xl font-bold mb-3" data-id="element-1153">
                            Competitive Positioning
                          </h4>
                          <p className="text-sm sm:text-base text-white/80 leading-relaxed" data-id="element-1154">
                            Design recommendations helped position Unisys better
                            against competitors like Illumio by simplifying
                            complex workflows.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </div>

          {!isOverlay && <CaseStudyNavigation homePath={homePath} previousCase={{
          category: 'Case Studies',
          title: 'U.S. Bank',
          slug: 'us-bank'
        }} nextCase={{
          category: 'Case Studies',
          title: 'AI Comparison',
          slug: 'ai-comparison'
        }} data-id="element-1155" />}

          {!isOverlay && <Footer data-id="element-1156" />}
        </div>

        {!isOverlay && <ChatDrawer onCaseStudyClick={handleCaseStudyClick} data-id="element-1157" />}
      </div>

      {!isOverlay && !isChatDrawerOpen && <FloatingButtons data-id="element-1158" />}
    </>;
}